"use client";

import Link from "next/link";
import { HiChevronRight, HiHome } from "@/components/icons";

export interface BreadcrumbItem {
  name: string;
  path: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface BreadCrumbProps {
  items: BreadcrumbItem[];
}

/**
 * Generate breadcrumbs from a pathname
 */
export function generateBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split("/").filter(Boolean);
  const breadcrumbs: BreadcrumbItem[] = [{ name: "Home", path: "/", icon: HiHome }];

  // Route segments with no index page of their own. Linking them would produce
  // a broken breadcrumb (e.g. /redirect/<destination>/ has no /redirect/ page,
  // /author/<slug>/ has no /author/ page).
  const segmentsWithoutIndex = new Set(["redirect", "author"]);

  let currentPath = "";
  segments.forEach((segment) => {
    currentPath += `/${segment}`;
    if (segmentsWithoutIndex.has(segment)) return;

    // Format segment names nicely
    let name = segment.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

    // Slugs are permanent — they are indexed URLs — but the product naming rule
    // changed in Aug 2026. Title-casing a legacy slug such as
    // "does-instagram-unfollow-pro-affect-engagement" would render the old fused
    // name in the breadcrumb even though every content source now uses the new
    // one. Rewrite the derived label so what the user reads matches the product,
    // while the URL underneath is untouched.
    const LEGACY_PRODUCT_NAMES: ReadonlyArray<readonly [RegExp, string]> = [
      [/\bInstagram Followers Exporter Pro\b/g, "Followers Exporter Pro for Instagram"],
      [/\bLinkedIn Connection Remover Pro\b/g, "Connection Remover Pro for LinkedIn"],
      [/\bX Followers Exporter Pro\b/g, "Followers Exporter Pro for X"],
      [/\bTradingView Screener Pro\b/g, "Screener Pro for TradingView"],
      [/\bInstagram Unfollow Pro\b/g, "Unfollow Pro for Instagram"],
      [/\bReddit Scout Pro\b/g, "Scout Pro for Reddit"],
      [/\bX Unfollow Pro\b/g, "Unfollow Pro for X"],
    ];
    for (const [pattern, replacement] of LEGACY_PRODUCT_NAMES) {
      name = name.replace(pattern, replacement);
    }

    // Special formatting for known routes
    // Ensure all paths have trailing slashes (except root)
    let path = currentPath + "/";

    // Singular route segments → plural hub paths
    const singularToPlural: Record<string, { name: string; hubPath: string }> = {
      product: { name: "Products", hubPath: "/products/" },
      tool: { name: "Free Tools", hubPath: "/tools/" },
      alternative: { name: "Alternatives", hubPath: "/alternatives/" },
      comparison: { name: "Comparisons", hubPath: "/comparisons/" },
      guide: { name: "Guides", hubPath: "/guides/" },
      download: { name: "Downloads", hubPath: "/downloads/" },
      prompt: { name: "Prompts", hubPath: "/prompts/" },
    };

    // Segments that need custom display names (but keep their own path)
    const displayNames: Record<string, string> = {
      best: "Roundups",
      faq: "FAQ",
      learn: "Learn",
      blog: "Blog",
      install: "Install",
      pricing: "Pricing",
      "custom-extension-quote": "Custom Extension Quote",
      "privacy-policy": "Privacy Policy",
      "return-policy": "Return Policy",
      "terms-of-service": "Terms of Service",
      "thank-you": "Thank You",
    };

    // Product slug display names
    const productNames: Record<string, string> = {
      "x-unfollow-pro": "Unfollow Pro for X",
      "x-followers-exporter-pro": "Followers Exporter Pro for X",
      "video-downloader-pro": "Video Downloader Pro",
      "prompt-anything-pro": "Prompt Anything Pro",
      "instagram-unfollow-pro": "Unfollow Pro for Instagram",
      "instagram-followers-exporter-pro": "Followers Exporter Pro for Instagram",
      "reddit-scout-pro": "Scout Pro for Reddit",
      "readmonkey-pro": "ReadMonkey Pro",
      "enhanced-pdf-viewer": "Enhanced PDF Viewer",
      "linkedin-connection-remover-pro": "Connection Remover Pro for LinkedIn",
    };

    if (singularToPlural[segment]) {
      name = singularToPlural[segment].name;
      path = singularToPlural[segment].hubPath;
    } else if (displayNames[segment]) {
      name = displayNames[segment];
    } else if (productNames[segment]) {
      name = productNames[segment];
    }

    breadcrumbs.push({
      name,
      path,
    });
  });

  return breadcrumbs;
}

/**
 * BreadCrumb component for displaying navigation breadcrumbs
 */
export default function BreadCrumb({ items }: BreadCrumbProps) {
  if (items.length === 0) return null;

  return (
    <div className="border-b bg-muted/30">
      <div className="container py-2">
        <nav aria-label="Breadcrumb" className="flex items-center text-sm">
          <ol className="flex items-center gap-1 text-muted-foreground">
            {items.map((crumb, index) => {
              const isLast = index === items.length - 1;
              return (
                <li key={crumb.path || index} className="flex items-center gap-1">
                  {index > 0 && <HiChevronRight className="h-3 w-3" />}
                  {isLast ? (
                    <span className="font-medium text-foreground flex items-center gap-1">
                      {crumb.icon && <crumb.icon className="h-4 w-4" />}
                      {crumb.name}
                    </span>
                  ) : (
                    <Link
                      href={crumb.path}
                      className="hover:text-primary transition-colors flex items-center gap-1"
                    >
                      {crumb.icon && <crumb.icon className="h-4 w-4" />}
                      {crumb.name}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      </div>
    </div>
  );
}
